import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { fmtMoney } from "../../lib/clarity";

const B = [
  { k: "taxable", label: "Taxable", color: "#9CA3AF" },
  { k: "deferred", label: "Tax-deferred", color: "#C5A059" },
  { k: "free", label: "Tax-free / potentially tax-free", color: "#34D399" },
];

export function BucketBar({ shares }) {
  return (
    <div>
      <div className="flex h-4 w-full bg-exodus-border overflow-hidden">
        {B.map((b) => <div key={b.k} style={{ width: `${shares[b.k] || 0}%`, background: b.color, transition: "width 1s ease-out" }} />)}
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3">
        {B.map((b) => (
          <span key={b.k} data-testid={`bucket-share-${b.k}`} className="text-xs text-exodus-muted flex items-center gap-2">
            <span className="w-2.5 h-2.5" style={{ background: b.color }} /> {b.label}: <span className="text-exodus-text">{b.k in shares ? `${shares[b.k]}%` : "Unknown"}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ReportTaxRetire({ tax, retirement }) {
  const pj = retirement.projection;
  return (
    <section className="grid lg:grid-cols-2 gap-5">
      <div data-testid="report-tax-buckets" className="card-surface p-6 sm:p-8">
        <p className="eyebrow">Tax-bucket analysis</p>
        <h3 className="font-display text-2xl mt-2 mb-6">Where your money lives</h3>
        <BucketBar shares={tax.shares} />
        <ul className="mt-6 space-y-3 text-sm text-exodus-text/90">
          {tax.observations.map((o, i) => <li key={i} className="flex gap-2"><span className="text-exodus-gold">—</span>{o}</li>)}
        </ul>
        <p className="text-xs text-exodus-muted mt-5">{tax.note}</p>
        <Link to="/tax-buckets" data-testid="report-tax-link" className="mt-6 inline-flex items-center gap-2 text-sm gold-text hover:underline">
          Explore the three tax buckets <ArrowRight size={14} />
        </Link>
      </div>
      <div data-testid="report-retirement" className="card-surface p-6 sm:p-8">
        <p className="eyebrow">Retirement readiness</p>
        <h3 className="font-display text-2xl mt-2 mb-6">Your timeline, in context</h3>
        <p className="text-sm text-exodus-muted">Reported savings across all categories (range midpoints): <span className="text-exodus-text">{fmtMoney(retirement.current_savings)}</span></p>
        {pj ? (
          <div className="mt-6 border border-exodus-border p-5">
            <p className="eyebrow text-[10px] text-exodus-muted">Illustrative projection · based on your inputs</p>
            <p data-testid="report-projection-balance" className="font-display text-4xl gold-text mt-2">{fmtMoney(pj.projected_balance)}</p>
            <p className="text-xs text-exodus-muted mt-1">in about {pj.years} years · roughly {fmtMoney(pj.illustrative_annual_income)}/yr at a {pj.withdrawal_rate * 100}% withdrawal</p>
            <ul className="mt-4 space-y-1 text-[11px] text-exodus-muted list-disc pl-4">{pj.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
          </div>
        ) : (
          <p className="text-sm text-exodus-muted mt-6">Not enough inputs for an illustrative projection. Income range, retirement timeframe, savings rate, and all three bucket balances are needed.</p>
        )}
        {retirement.observations.length > 0 && (
          <ul className="mt-5 space-y-2 text-sm text-exodus-text/90">
            {retirement.observations.map((o) => <li key={o} className="flex gap-2"><span className="text-exodus-gold">—</span>{o}</li>)}
          </ul>
        )}
      </div>
    </section>
  );
}
